import { yupResolver } from '@hookform/resolvers/yup';
import { FormProvider, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useQuery } from 'react-query';

import { useState } from 'react';

import { Button, DialogActions, Tooltip } from '@mui/material';

import { getBalanceBudgetYearOverview } from 'RestAPIs/employees/employment-hours';

import { ArrowNavigation } from 'components/shared';

import { DetailDialog } from '../../EmploymentHours.styles';
import { formatPeriodMonth } from '../../EmploymentHours.utils';
import { editableCorrectionKeys } from '../HoursCorrectionDialog/correctionSpec';
import { buildDefaultValues } from '../HoursCorrectionDialog/hoursCorrection.utils';
import { getHoursCorrectionSchema } from '../HoursCorrectionDialog/hoursCorrectionSchema';
import { useSaveHoursCorrection } from '../HoursCorrectionDialog/useSaveHoursCorrection';
import HoursRowDetailPanel from '../HoursRowDetailPanel/HoursRowDetailPanel';
import MemoAndSalaryTrigger from './MemoAndSalaryTrigger';

import type { GridRowId } from '@mui/x-data-grid-pro';
import type {
  ClaYearInfo,
  EmploymentHoursGridRow
} from '../../EmploymentHours.types';
import type { HoursCorrectionValues } from '../HoursCorrectionDialog/hoursCorrection.utils';

/** The balance-budget section belongs to the last period of the year only (`…ViewModel.js:112`). */
const LAST_PERIOD = 12;

const FORM_ID = 'hours-corrections-form';

type Props = {
  /** the month rows of the selected year, in grid order — the window navigates between them */
  rows: EmploymentHoursGridRow[];
  /** the row whose action was clicked */
  rowId: GridRowId;
  employeeId: number;
  workAgreementId: number;
  year: number;
  claYear?: ClaYearInfo;
  showPlusMinus: boolean;
  /** the hours-correction grant; without it the window is read-only, as in Dojo's READ_ONLY mode */
  canEdit: boolean;
  canViewBalanceBudget: boolean;
  onClose: () => void;
};

/**
 * One month, in a window instead of the expanding row it used to be, and the only place corrections
 * are made — it replaces the separate correction form (`EmployeeHoursCorrectionForm.js`).
 *
 * Every correction sits beside the value it corrects, in that value's group. Only the two that are not
 * numbers — the memo and the salary-export trigger — sit apart, at the bottom.
 */
const HoursDetailDialog = ({
  rows,
  rowId,
  employeeId,
  workAgreementId,
  year,
  claYear,
  showPlusMinus,
  canEdit,
  canViewBalanceBudget,
  onClose
}: Props) => {
  'use memo';

  const { t } = useTranslation('common');
  const [selectedId, setSelectedId] = useState(rowId);

  const ids = rows.map((row) => row.id);
  // the year selector sits behind the modal, so the row set cannot change while this is open
  const row = rows.find((candidate) => candidate.id === selectedId) ?? rows[0];

  const showBalanceBudget = row.pPeriod === LAST_PERIOD && canViewBalanceBudget;

  const { data: balanceBudget } = useQuery({
    queryKey: ['employmentHoursBalanceBudget', workAgreementId, year],
    queryFn: () => getBalanceBudgetYearOverview(workAgreementId, year),
    enabled: showBalanceBudget
  });

  const context = { flags: claYear?.flags, showBalanceBudget };
  const editableKeys = editableCorrectionKeys(context);

  const methods = useForm<HoursCorrectionValues>({
    defaultValues: buildDefaultValues(row, editableKeys, showPlusMinus),
    resolver: yupResolver(getHoursCorrectionSchema(editableKeys, t))
  });

  const { mutate: save, isLoading: isSaving } = useSaveHoursCorrection({
    employeeId,
    workAgreementId,
    // guarded on the save button: a period with no dty_duty_line_year row cannot be corrected
    dutyLineMngId: row.dutyLineMngId as number,
    year,
    showPlusMinus,
    onSaved: onClose
  });

  const goToMonth = (nextId: GridRowId) => {
    const next = rows.find((candidate) => candidate.id === nextId);

    if (next) {
      setSelectedId(nextId);
      methods.reset(buildDefaultValues(next, editableKeys, showPlusMinus));
    }
  };

  const { isDirty } = methods.formState;
  const month = formatPeriodMonth(row.valid?.from);

  return (
    <DetailDialog
      open
      fullWidth
      maxWidth="large"
      handleClose={onClose}
      title={
        // ponytail: unsaved changes block the arrows rather than prompting; a confirm dialog can
        // replace this if switching month mid-edit turns out to be common
        <Tooltip title={isDirty ? t('employmentHours.detail.saveFirst') : ''}>
          <span>
            <ArrowNavigation
              options={ids}
              selectedOption={selectedId}
              disabled={isDirty}
              onNavigate={(direction, index) =>
                goToMonth(ids[direction === 'prev' ? index - 1 : index + 1])
              }
              prevLabel={t('employmentHours.detail.previousMonth')}
              nextLabel={t('employmentHours.detail.nextMonth')}
            >
              {`${t('employmentHours.detail.title')} ${month} ${year}`}
            </ArrowNavigation>
          </span>
        </Tooltip>
      }
      actions={
        <DialogActions>
          <Button onClick={onClose} color="secondary" variant="contained">
            {t('cancel')}
          </Button>
          {canEdit && (
            <Button
              form={FORM_ID}
              type="submit"
              loading={isSaving}
              disabled={!row.dutyLineMngId}
              color="primary"
              variant="contained"
            >
              {t('save')}
            </Button>
          )}
        </DialogActions>
      }
    >
      <FormProvider {...methods}>
        <form
          id={FORM_ID}
          onSubmit={methods.handleSubmit((values) => save(values))}
        >
          <HoursRowDetailPanel
            row={{ ...row, ...balanceBudget }}
            flags={claYear?.flags}
            plbName={claYear?.plbName}
            selectedYear={year}
            editableKeys={editableKeys}
            canEdit={canEdit}
            showPlusMinus={showPlusMinus}
            showBalanceBudget={showBalanceBudget}
          />
          <MemoAndSalaryTrigger canEdit={canEdit} />
        </form>
      </FormProvider>
    </DetailDialog>
  );
};

export default HoursDetailDialog;
